import {v2} from "oc-threejs/short3";
import Line2d from "oc-threejs/math/Line2d";

window.Line2d = Line2d;

/**
 * Dachschrägen
 */
export default class Slopings {

    sx: ?Slopings.Sloping;
    sz: ?Slopings.Sloping;
    yMax: number;

    constructor(yMax) {
        this.yMax = yMax;
    }

    /**
     * Gibt die maximale Y-Koordinate zurück, bei der die Senkrechte mit X-Koordinate x die Schräge oder den Deckel schneidet.
     * dy: Breite des Deckels, verschiebt die Waagerechte oben ein Stück nach unten
     * ds: Breite des Schräg-Brettes, verschiebt die Schräge ein Stück nach innen. Ist gleich dy falls nicht definiert.
     */
    yOfX(x, dy=0, ds=undefined) {
        if (ds===undefined) {
            ds = dy;
        }
        const y = this.sx ? this.sx.yOfX(x,ds) : this.yMax;
        return Math.min(y,this.yMax-dy);
    }

    /**
     * Gibt die X-Koordinate zurück, bei der die Waagerechte mit Y-Koordinate y die Schräge schneidet.
     * Falls keine Schräge existiert oder diese nicht geschnitten wird, wird POSITIVE_INFINITY zurückgegeben.
     * Mit ds wird die Schräge für den Schnitttest parallel zur ursprünglichen Schräge verschoben
     * (Brettstärke, in Richtung der Normalen, d.h. ins Schrankinnere).
     */
    xOfY(y, ds=0) {
        return this.sx ? this.sx.xOfY(y,ds) : Number.POSITIVE_INFINITY;
    }

    /**
     * gibt die Element-Tiefe (genauer die hintereste Z-Position) an der Höhe (Position) y zurück
     */
    elementZofY(y) {
        return this.zOfY(y, APP.settings.getFloat('rueckwand_vorsprung'), APP.settings.getFloat('deckel_staerke'));
    }

    /**
     * gibt die Element-Höhe (genauer die oberste Y-Position) an der Tiefe (Position) z zurück
     */
    elementYofZ(z) {
        const deckelStaerke = APP.settings.getFloat('deckel_staerke');
        return this.yOfZ(z, deckelStaerke, deckelStaerke);
    }

    /**
     * Gibt die maximale Y-Koordinate zurück, bei der die Senkrechte mit Z-Koordinate z die Schräge oder den Deckel schneidet.
     * dy: Breite des Deckels, verschiebt die Waagerechte oben ein Stück nach unten
     * ds: Breite des Schräg-Brettes, verschiebt die Schräge ein Stück nach innen. Ist gleich dy falls nicht definiert.
     */
    yOfZ(z, dy=0, ds=undefined) {
        if (ds===undefined) {
            ds = dy;
        }
        const y = this.sz ? this.sz.yOfX(z,ds) : this.yMax;
        return Math.min(y,this.yMax-dy);
    }

    /**
     * Gibt die Z-Koordinate zurück, bei der die Waagerechte mit Y-Koordinate y die Schräge schneidet.
     * Falls keine Schräge existiert oder diese nicht geschnitten wird, wird POSITIVE_INFINITY zurückgegeben.
     * Mit ds wird die Schräge für den Schnitttest parallel zur ursprünglichen Schräge verschoben
     * (Brettstärke, in Richtung der Normalen, d.h. ins Schrankinnere).
     * dx: Abstand von hinten (Rückwand + Vorsprung)
     * ds: Breite des Schräg-Brettes, verschiebt die Schräge ein Stück nach innen. Ist gleich dy falls nicht definiert.
     */
    zOfY(y, dx=0, ds=0) {
        const x = this.sz ? this.sz.xOfY(y,ds) : Number.POSITIVE_INFINITY;
        return Number.isFinite(x) ? Math.max(x,dx) : dx;
    }

    /**
     * Gibt zurück, ob innerhalb der Box eine (links oder rechts) Schräge vorkommt
     */
    isSloped(box,withY=false) {
        const epsilon = 0.0001;
        const y1 = this.yOfX(box.min.x, 0);
        const y2 = this.yOfX(box.max.x, 0);
        return Math.abs(y1 - y2) > epsilon && (!withY || box.max.y > Math.min(y1,y2)-epsilon);
    }

    /**
     * Gibt zurück, ob innerhalb der Box ein Knick vorkommt, also eine (links oder rechts) Schräge in eine Waagerechte übergeht
     */
    isBroken(box) {
        return this.sx && (this.sx.containsX(box.min.x) != this.sx.containsX(box.max.x));
    }

    static Sloping = class {

        constructor(x0, y0, x1, y1) {
            this.start = v2(x0, y0);
            this.end = v2(x1, y1);
            this.direction = this.end.clone().sub(this.start).normalize();
        }

        containsX(x) {
            return this.start.x <= x && x <= this.end.x;
        }

        yOfX(x, sd) {
            const dx = x - this.start.x;
            const dy = this.direction.y / this.direction.x * dx;
            return this.start.y + dy - sd * (this.direction.x + this.direction.y * this.direction.y / this.direction.x);
        }

        xOfY(y, sd) {
            const d = this.direction;
            const yd = sd * (d.x + d.y * d.y / d.x);
            const dy = y - (this.start.y - yd);
            return this.start.x + dy * d.x / d.y;
        }

    };

}

